/**
 * Cave lighting — how the torch responds to depth, fuel and the player's energy.
 *
 * Spec: docs/CAVE_VIEW.md §3. Pure functions only, same as `projection.ts`, so
 * the behaviour can be tested without a renderer. A lighting bug looks exactly
 * like an art bug and is miserable to chase through a screenshot.
 *
 * The model has one hard rule that overrides every other consideration:
 *
 *   **The cave never goes black.** Every output is floored. A screen the player
 *   cannot read does not communicate "your torch is dying" — it communicates
 *   "this app is broken", and they close it. Darkness is a mood, not a failure
 *   state, and the floors below are what keep the difference.
 *
 * Three inputs, three different jobs:
 *
 *   `torchPct`  fuel — the resource the player spends. Drives radius and flicker,
 *               because a guttering torch is the diegetic warning to turn back.
 *   `energyPct` stamina — drives a gentle desaturation only. It must NOT shrink
 *               the light: energy is a meta-currency, and punishing legibility
 *               for it reads as a rendering fault rather than a game state.
 *   `depth`     drives colour and wetness. Deep rock is damp and cold; the warm
 *               candle near the surface becomes a small warm island a long way
 *               down. This is the only input that makes depth *feel* different.
 */

export interface LightInputs {
  /** The lamp's `light_px` — the equipment ceiling. */
  lightPx: number;
  /** Torch fuel remaining, 0–100. */
  torchPct: number;
  /** Player energy remaining, 0–100. */
  energyPct: number;
  /** Metres down. */
  depth: number;
  /**
   * The player's own brightness setting, `BRIGHTNESS_MIN`–`BRIGHTNESS_MAX`.
   *
   * Not a game state and not an equipment stat: a phone at 30% screen brightness in
   * daylight and an OLED in a dark room are two different displays, and no single
   * authored curve is right for both. It scales the light and *divides* the darkness,
   * so turning it up opens the cave rather than washing a grey film over it.
   *
   * It cannot reach either extreme: `DARK_CEILING` stops the low end going black and
   * `shade()` floors the high end above transparent, so no value of this makes the
   * cave unreadable in either direction.
   */
  brightness?: number;
  /**
   * What the far dark fades to, as `#rrggbb` — the cave's own `fog_hex`.
   *
   * Optional, because a server that predates the cave palette sends no fog and the
   * default has to be a colour rather than a crash.
   */
  fog?: string;
}

export interface LightState {
  /** Effective light radius in px, after fuel burn-down. */
  radius: number;
  /** Warm-glow radius; always inside `radius`. */
  glowRadius: number;
  /** 0–1 flicker amplitude. Rises sharply as fuel runs out. */
  flicker: number;
  /** Seconds per flicker cycle. Shorter = more agitated. */
  flickerSeconds: number;
  /** 0–1 wet-rock sheen. Rises with depth. */
  sheen: number;
  /** rgb triple for the warm core of the light. */
  warm: [number, number, number];
  /** 0–1 extra vignette darkness from low energy. */
  fatigue: number;
  /** True when the torch is low enough that the UI should say so. */
  guttering: boolean;
  /** Directional torch focal X position ('22%'). */
  directionalX: string;
  /** Directional torch focal Y position ('68%'). */
  directionalY: string;
  /** 0–0.45 depth ambient darkness dimmer factor. */
  depthDimmer: number;
  /** 1.0–2.2 directional lamp beam brightness intensity. */
  beamIntensity: number;
  /** The clamped brightness setting actually in force. */
  brightness: number;
  /**
   * The colour the dark fades to — **never pure black**.
   *
   * The cave's own fog, floored channel by channel. Every shadow in the corridor used
   * to be `rgba(0,0,0,α)`, which is not "dim", it is *absent*: at α close to 1 the
   * screen has no information in it at all, and a player reads that as the renderer
   * having stopped rather than as a torch that only reaches so far. A dim warm grey
   * at the same alpha still carries the wall's shape.
   */
  voidRgb: [number, number, number];
  /**
   * Darkness alpha → the alpha to actually paint, after the brightness setting.
   *
   * Every subtractive stop in `TorchLight` goes through this, so there is one place
   * that decides how dark this cave is allowed to get and one constant
   * (`DARK_CEILING`) enforcing that it never gets all the way there.
   */
  shade: (alpha: number) => number;
}

/** The dimmest and brightest the player may set the cave. */
export const BRIGHTNESS_MIN = 0.7;
export const BRIGHTNESS_MAX = 1.6;
export const BRIGHTNESS_DEFAULT = 1;

/**
 * The darkest any single stop may be painted, whatever depth, fatigue and the
 * brightness slider ask for between them.
 *
 * This is the hard rule at the top of this file, expressed as a number. Three
 * independent inputs multiply into the vignette — fuel, depth and energy — and each
 * was reasonable alone; together they had already pushed the far edge past total
 * black twice, and both times it was reported as a rendering fault rather than as
 * darkness. A ceiling is the only thing that survives someone later tuning any one
 * of them upward.
 */
export const DARK_CEILING = 0.86;

/**
 * The lowest any channel of the void colour may fall.
 *
 * `#000000` in a cave palette is not a dark colour, it is the absence of one. Even
 * the deepest zone keeps a few counts of warmth so the wall behind the shadow still
 * has a shape.
 *
 * 5, because that is the blue channel of `--cave-void` (`#0B0705`) — the darkest
 * colour the art direction actually endorses. A higher floor would mean this module
 * quietly disagreeing with `tokens.css` about how dark the cave is allowed to be,
 * which is the kind of second opinion that ends in two palettes.
 */
export const VOID_FLOOR = 5;

export function clampBrightness(value: number | undefined): number {
  if (value == null || !Number.isFinite(value)) return BRIGHTNESS_DEFAULT;
  return Math.min(BRIGHTNESS_MAX, Math.max(BRIGHTNESS_MIN, value));
}

/**
 * `#rrggbb` → rgb, with every channel lifted off the floor.
 *
 * Anything unparseable falls back to the canonical `--cave-void`, because a bad hex
 * from the server must dim the cave, never blank it.
 */
export function voidRgb(fog?: string): [number, number, number] {
  const hex = /^#?([0-9a-f]{6})$/i.exec((fog ?? '').trim());
  /* The fallback goes through the same floor as everything else rather than being
     returned raw — so the "no channel is ever below VOID_FLOOR" guarantee holds by
     construction, not because the default happens to satisfy it today. */
  const n = hex?.[1] ? parseInt(hex[1], 16) : 0x0b0705;   // --cave-void
  return [
    Math.max(VOID_FLOOR, (n >> 16) & 0xff),
    Math.max(VOID_FLOOR, (n >> 8) & 0xff),
    Math.max(VOID_FLOOR, n & 0xff),
  ];
}

/**
 * A darkness alpha, after the player's brightness setting and the hard ceiling.
 *
 * Divides rather than subtracts, so the setting acts like a dimmer on the shadows
 * instead of a flat grey wash: at 1.6 an 0.8 stop becomes 0.5 and the deep shadow
 * opens up, while a stop that was already faint stays faint.
 */
export function shade(alpha: number, brightness: number): number {
  return Math.min(DARK_CEILING, clamp01(alpha / clampBrightness(brightness)));
}

/** Depth at which the cold/damp shift is fully applied. */
export const COLD_DEPTH = 900;

/** Below this much fuel the torch visibly struggles. */
export const GUTTER_PCT = 25;

/**
 * Fuel floor for the radius. At 0% fuel the player still sees a metre of rock —
 * enough to read the "torch burned out" card and find the surface button.
 */
export const MIN_RADIUS_FRACTION = 0.34;

export function clamp01(n: number): number {
  return Math.min(1, Math.max(0, n));
}

const lerp = (a: number, b: number, t: number): number => a + (b - a) * t;

/**
 * Fuel → radius multiplier.
 *
 * Deliberately non-linear: the first half of the torch barely dims, then it
 * falls away. A linear burn-down means the player notices nothing until it is
 * too late to walk back; this curve gives them a visible warning with enough
 * fuel left to act on it.
 */
export function fuelFalloff(torchPct: number): number {
  const t = clamp01(torchPct / 100);
  const curve = t * t * (3 - 2 * t);          // smoothstep
  return MIN_RADIUS_FRACTION + (1 - MIN_RADIUS_FRACTION) * curve;
}

/**
 * How cold and damp it is here, 0 at the surface → 1 deep.
 *
 * `tanh`, not a linear ramp, for the same reason `throat()` uses it in
 * projection.ts: the cave is endless, so the curve has to saturate rather than
 * run off past its intended range.
 */
export function chill(depth: number): number {
  return clamp01(Math.tanh(Math.max(0, depth) / COLD_DEPTH));
}

/** Wet-rock specular. Deep rock is damp, and damp rock catches the torch. */
export function sheen(depth: number): number {
  return 0.06 + 0.34 * chill(depth);
}

/**
 * The warm core colour of the flame.
 *
 * Near the surface it is candle-warm (255, 196, 120). Deep down it cools toward
 * a paler, bluer cast — the same flame reading differently against cold wet
 * stone, which is what sells depth without changing the geometry.
 */
export function warmth(depth: number): [number, number, number] {
  const c = chill(depth);
  return [
    Math.round(lerp(255, 214, c)),
    Math.round(lerp(196, 198, c)),
    Math.round(lerp(120, 214, c)),
  ];
}

/**
 * Flicker amplitude. Steady while there is fuel, agitated once guttering.
 *
 * This is the honest warning channel: a flickering torch is information the
 * player can read at a glance, without a number or a modal.
 */
export function flicker(torchPct: number): number {
  if (torchPct >= GUTTER_PCT) {
    // A living flame is never perfectly still, but it should not distract.
    return 0.05;
  }
  const panic = 1 - clamp01(torchPct / GUTTER_PCT);
  return 0.05 + 0.45 * panic;
}

/**
 * Low energy dims the *edges*, never the centre.
 *
 * Tunnel vision when you are exhausted is a readable, familiar language. Cutting
 * overall brightness would be the wrong tool: the player would read it as the
 * torch dying and walk back to refuel a torch that is fine.
 */
export function fatigue(energyPct: number): number {
  const low = clamp01((35 - energyPct) / 35);
  return 0.30 * low * low;
}

/**
 * Depth ambient dimmer factor — rock dims progressively with cave depth.
 *
 * Was 0.42: compounded with TorchLight's own vignette and multiply-cone stops,
 * a deep run pushed everything past ~80% of the torch radius to functionally
 * total black — "the cave is dark" read as "the screen is broken". 0.30 keeps
 * depth reading as colder and dimmer without erasing the rock at the edges of
 * the light, which is the whole reason a wider cone was worth drawing.
 */
export function depthDimmer(depth: number): number {
  return 0.30 * clamp01(Math.tanh(Math.max(0, depth) / 750));
}

/**
 * Where the torch hangs, as a fraction of the viewport.
 *
 * Left of centre and a little above the middle: the miner carries it in the near
 * hand, so the corridor is lit from the side rather than from the camera, and the
 * right-hand wall falls into shadow. Exported as numbers because two callers need
 * it as maths and not only as a CSS percentage — `TorchLight` places the gradients,
 * and `RockField` casts each rock's shadow directly away from it. A rock lit from
 * one direction while the cave is lit from another is the tell that a sprite has
 * been pasted on rather than placed in the scene.
 */
export const TORCH_U = 0.30;
export const TORCH_V = 0.54;

/** Directional lamp beam intensity based on lamp level and fuel. */
export function beamIntensity(lightPx: number, torchPct: number): number {
  const levelFactor = clamp01(lightPx / 680);
  const fuelFactor = fuelFalloff(torchPct);
  return (1.0 + 1.25 * levelFactor) * fuelFactor;
}

export function lightState(input: LightInputs): LightState {
  const { lightPx, torchPct, energyPct, depth } = input;
  const brightness = clampBrightness(input.brightness);

  /* The setting moves the light's reach as well as the shadows' depth. Turning the
     cave up only by lifting the black would have produced a grey, flat cave — the
     torch has to actually reach further for the room to feel brighter. Capped well
     under the raw multiplier so the highest setting is still a torch. */
  const radius = Math.max(40, lightPx * fuelFalloff(torchPct) * (0.82 + 0.18 * brightness));
  const amp = flicker(torchPct);

  return {
    radius,
    glowRadius: radius * 0.8,
    flicker: amp,
    // Guttering flames move faster as well as further.
    flickerSeconds: lerp(3.6, 0.9, clamp01((amp - 0.05) / 0.45)),
    sheen: sheen(depth),
    warm: warmth(depth),
    fatigue: fatigue(energyPct),
    guttering: torchPct < GUTTER_PCT,
    directionalX: `${TORCH_U * 100}%`,
    directionalY: `${TORCH_V * 100}%`,
    depthDimmer: depthDimmer(depth),
    beamIntensity: beamIntensity(lightPx, torchPct) * (0.86 + 0.14 * brightness),
    brightness,
    voidRgb: voidRgb(input.fog),
    shade: (alpha: number) => shade(alpha, brightness),
  };
}

/** `rgba(...)` for the cave's void colour at a given darkness alpha. */
export function voidRgba(rgb: [number, number, number], alpha: number): string {
  return `rgba(${rgb[0]},${rgb[1]},${rgb[2]},${alpha.toFixed(3)})`;
}

/** `rgba(...)` for the warm core at a given alpha. */
export function warmRgba(warm: [number, number, number], alpha: number): string {
  return `rgba(${warm[0]},${warm[1]},${warm[2]},${alpha})`;
}
